// The tip: amount tiles + custom pad, the debounced PaymentIntent, the
// wallet-direct tap (one PaymentRequest per open, update as the amount
// changes, show() inside the gesture), and the success tail. Deliberate twin
// of hooks/useBuyCheckout — keep the two in step when either changes. Split
// out of pages/RequestPage.tsx; TipCheckout renders it.
import { useEffect, useMemo, useRef, useState } from "react";
import type { PaymentRequest as StripePaymentRequest } from "@stripe/stripe-js";
import { paymentsApi } from "../services/payments";
import { calculateFeeBreakdown, round2 } from "../utils/feeCalculator";
import { roundPriceStep } from "../utils/roundPriceStep";
import { configFor, defaultPriceFor, maxPriceFor, minPriceFor } from "../utils/payinConfig";
import { GreenIcon, SmileIcon, HeartIcon, StarIcon } from "../components/GreenIcons";
import { STRIPE_PK, stripePromise, customerCopyForPayError } from "../lib/fanCheckout";
import { IS_NATIVE } from "../lib/stripeFlow";
import { t as tr } from "../i18n";

/* Tip tile ladder — THREE presets + Custom (founder, "MORE SIMPLE";
   was six). Derived from the SAME per-currency config as every other
   amount: [c0, 2×c0, c1] lands on clean local amounts in all twelve
   currencies (USD 5/10/20 · SEK 50/100/200 · HUF 2000/4000/8000). Never
   hardcode dollars. The ascending-distinct filter guards future config
   edits. Custom (NumberPad) covers everything above the ladder. */
// Duotone brand icons, ascending with the amount (founder C3 pick) —
// the same three faces these amounts wore on the old six-tile ladder.
const TIP_TILE_ICONS: GreenIcon[] = [SmileIcon, HeartIcon, StarIcon];
function tipTilesFor(currency?: string | null): number[] {
  const cfg = configFor(currency);
  const raw = [cfg.chips[0], cfg.chips[0] * 2, cfg.chips[1]];
  const out: number[] = [];
  for (const a of raw) {
    if (Number.isFinite(a) && a > 0 && (out.length === 0 || a > out[out.length - 1])) {
      out.push(a);
    }
  }
  return out;
}

type Args = {
  dj: any;
  viewDj: any;
  isProfilePreview: boolean;
  comment: string;
  clientNickname: string;
  clientInstagram: string;
};

export function useTipCheckout({ dj, viewDj, isProfilePreview, comment, clientNickname, clientInstagram }: Args) {
  const [tipMode, setTipMode] = useState(false);
  const [tipAmount, setTipAmount] = useState("5");
  // Facelift (founder pick A): tiles are the primary input; the pad
  // opens via Custom; payment reveals only after the "Send Tip · $X" tap —
  // exact twins of the Buy Playlist states below.
  const [tipPadOpen, setTipPadOpen] = useState(false);
  const [tipDraft, setTipDraft] = useState("");
  const [tipCtaTapped, setTipCtaTapped] = useState(false);
  const [tipOrderId, setTipOrderId] = useState<string | null>(null);
  const [tipClientSecret, setTipClientSecret] = useState<string | null>(null);
  const [tipShowPay, setTipShowPay] = useState(false);
  const [tipShowCard, setTipShowCard] = useState(false);
  const [tipProcessing, setTipProcessing] = useState(false);
  const [tipPayError, setTipPayError] = useState<string | null>(null);
  const tipDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** === Tip payment intent === */
  const tipBase = (() => {
    const raw = Number(tipAmount || defaultPriceFor(viewDj?.currency));
    const floor = minPriceFor(viewDj?.currency);
    return isNaN(raw) || raw < floor ? floor : roundPriceStep(raw, viewDj?.currency);
  })();
  const tipBreakdown = calculateFeeBreakdown(tipBase, viewDj?.currency);
  const tipTotal = tipBreakdown.customerTotal;

  useEffect(() => {
    if (isProfilePreview) return;
    if (!tipMode || !dj?.slug || (!stripePromise && STRIPE_PK)) return;
    if (tipBase < 1) return;

    setTipShowPay(false);
    setTipClientSecret(null);
    setTipShowCard(false);
    setTipPayError(null);

    const reqId = (window.crypto?.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "");
    (window as any).__rq_last_tip = reqId;

    if (tipDebounceRef.current) clearTimeout(tipDebounceRef.current);
    tipDebounceRef.current = setTimeout(async () => {
      try {
        const data = await paymentsApi.createTipIntent({
            djSlug: dj.slug,
            amount: tipTotal,
            requestAmount: tipBase,
            comment,
            clientNickname: clientNickname || undefined,
            clientInstagram: clientInstagram || undefined,
            orderId: tipOrderId || undefined,
        });
        if ((window as any).__rq_last_tip !== reqId) return;
        tipSecretBaseRef.current = tipBase; // which amount this secret is for
        setTipClientSecret(data.clientSecret);
        setTipOrderId(data.orderId);
        setTipShowPay(true);
        setTipPayError(null);
        sessionStorage.setItem("lastDjSlug", dj.slug);
      } catch (e: any) {
        console.error("Tip intent error", e);
        // Code-aware copy; see customerCopyForPayError above.
        setTipPayError(customerCopyForPayError(e, dj?.name || ""));
      }
    }, 400);

    return () => {
      if (tipDebounceRef.current) clearTimeout(tipDebounceRef.current);
    };
    // Same rule as the request intent above: keyed on the amount, not on the
    // free-text fields that ride along at pay time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipMode, dj?.slug, tipBase, tipTotal, stripePromise, isProfilePreview]);

  /* ── Tip facelift machinery (founder pick A) — deliberate twin of
     the Buy Playlist block below (tiles / Custom pad / wallet-direct CTA);
     keep the two in step when either changes. ── */
  const tipTiles = useMemo(
    () =>
      tipTilesFor(viewDj?.currency).map((amount, i) => ({
        amount,
        Icon: TIP_TILE_ICONS[Math.min(i, TIP_TILE_ICONS.length - 1)],
      })),
    [viewDj?.currency]
  );
  const tipTileSelected = (amount: number) => Math.abs(tipBase - amount) < 0.005;
  const tipIsCustom = tipTiles.length > 0 && !tipTiles.some((t) => tipTileSelected(t.amount));

  const commitTipPad = () => {
    const t = tipDraft.trim();
    if (t !== "") {
      const n = Number(t.replace(",", "."));
      const floor = minPriceFor(viewDj?.currency);
      // Fraud door: the same request cap the server enforces (default tier,
      // or lifted for an admin-vouched DJ) — clamped here so the displayed
      // amount is honest immediately, same as the floor.
      const cap = maxPriceFor(viewDj?.currency, viewDj?.capsLifted);
      // Floor-clamped — tipBase snaps/floors again anyway, this keeps the
      // displayed amount honest immediately.
      if (Number.isFinite(n) && n > 0) {
        setTipAmount(String(Math.min(Math.max(round2(n), floor), cap)));
      }
    }
    setTipDraft("");
    setTipPadOpen(false);
  };

  // Shared tail of a successful tip — wallet-direct path and
  // InlinePayOptions' onPaymentSuccess both land here.
  const tipSucceeded = async (orderId: string, piId?: string) => {
    try {
      await paymentsApi.confirmIntent(orderId, piId);
    } catch (e) {
      console.error("confirm tip error", e);
    }
    setTipClientSecret(null);
    setTipShowPay(false);
    setTipMode(false);
    window.location.href = `/fan/requests?order=${encodeURIComponent(orderId)}`;
  };

  /* v2 (round 2) — exact twin of the buy machinery below: ONE
     PaymentRequest per sheet-open, update({total}) as amounts change, tap
     shows the sheet synchronously and resolves the PI while it's up. */
  const tipWalletRef = useRef<{
    pr: StripePaymentRequest;
    applePay: boolean;
    lastCents: number;
  } | null>(null);
  const tipWalletSeqRef = useRef(0);
  const [tipWalletReady, setTipWalletReady] = useState(false);
  const [tipWalletMark, setTipWalletMark] = useState(false);
  const tipSecretBaseRef = useRef<number | null>(null);
  const tipTapCtxRef = useRef<{
    secretPromise: Promise<{ clientSecret: string; orderId: string }>;
    cancelled: boolean;
    succeeded: boolean;
    uiSettled: boolean;
  } | null>(null);

  useEffect(() => {
    const seq = ++tipWalletSeqRef.current; // stales any in-flight arm
    tipWalletRef.current = null;
    tipTapCtxRef.current = null;
    setTipWalletReady(false);
    setTipWalletMark(false);
    // Never inside the app: the web wallet sheet cannot open in the web view
    // (see InlinePayOptions), so the green CTA takes the classic path there.
    if (isProfilePreview || !tipMode || !stripePromise || IS_NATIVE) return;
    const initialCents = Math.round(tipTotal * 100);
    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe || seq !== tipWalletSeqRef.current) return;
        const pr = stripe.paymentRequest({
          country: "US",
          currency: String(viewDj?.currency || "usd").toLowerCase(),
          total: { label: "Request App", amount: initialCents },
          requestPayerEmail: true,
          disableWallets: ["link"],
        });
        const avail = await pr.canMakePayment();
        if (seq !== tipWalletSeqRef.current) return;
        if (!avail) return; // no wallet on this device → classic flow

        pr.on("cancel", () => {
          const ctx = tipTapCtxRef.current;
          if (!ctx) return;
          ctx.cancelled = true;
          if (!ctx.succeeded && !ctx.uiSettled) {
            ctx.uiSettled = true;
            setTipCtaTapped(true);
          }
        });

        pr.on("paymentmethod", async (ev) => {
          const ctx = tipTapCtxRef.current;
          if (!ctx) {
            try { ev.complete("fail"); } catch {}
            return;
          }
          let clientSecret: string;
          let orderId: string;
          try {
            const d = await ctx.secretPromise;
            clientSecret = d.clientSecret;
            orderId = d.orderId;
          } catch (err: any) {
            try { ev.complete("fail"); } catch {}
            if (!ctx.uiSettled) {
              ctx.uiSettled = true;
              setTipCtaTapped(true);
              setTipPayError(customerCopyForPayError(err, dj?.name || ""));
            }
            return;
          }
          if (ctx.cancelled) return;
          try {
            const stripeNow = stripePromise ? await stripePromise : null;
            if (!stripeNow || ctx.cancelled) {
              try { ev.complete("fail"); } catch {}
              return;
            }
            const result = await stripeNow.confirmCardPayment(
              clientSecret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            );
            if (result.error) {
              try { ev.complete("fail"); } catch {}
              if (!ctx.uiSettled) {
                ctx.uiSettled = true;
                setTipCtaTapped(true);
                setTipPayError(result.error.message || tr("useTipCheckout.paymentError"));
              }
              return;
            }
            ctx.succeeded = true;
            ctx.uiSettled = true;
            try { ev.complete("success"); } catch {}
            let pi = result.paymentIntent;
            if (pi && pi.status === "requires_action") {
              const next = await stripeNow.confirmCardPayment(clientSecret);
              if (next.error || !next.paymentIntent) {
                ctx.succeeded = false;
                setTipCtaTapped(true);
                setTipPayError(next.error?.message || tr("useTipCheckout.paymentError"));
                return;
              }
              pi = next.paymentIntent;
            }
            await tipSucceeded(orderId, pi?.id);
          } catch {
            try { ev.complete("fail"); } catch {}
            if (!ctx.succeeded && !ctx.uiSettled) {
              ctx.uiSettled = true;
              setTipCtaTapped(true);
              setTipPayError(tr("useTipCheckout.paymentError"));
            }
          }
        });

        tipWalletRef.current = { pr, applePay: !!avail.applePay, lastCents: initialCents };
        setTipWalletReady(true);
        setTipWalletMark(!!avail.applePay);
      } catch {
        if (seq === tipWalletSeqRef.current) {
          tipWalletRef.current = null;
          setTipWalletReady(false);
          setTipWalletMark(false);
        }
      }
    })();
    // Armed once per entry into tip mode. The live total is pushed to the
    // PaymentRequest as it changes (lastCents); re-arming per amount would
    // tear the wallet sheet down mid-tap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipMode, isProfilePreview]);

  useEffect(() => {
    const armed = tipWalletRef.current;
    if (!armed) return;
    const cents = Math.round(tipTotal * 100);
    if (cents === armed.lastCents) return;
    try {
      armed.pr.update({ total: { label: "Request App", amount: cents } });
      armed.lastCents = cents;
    } catch {}
  }, [tipTotal, tipWalletReady]);

  /** Tip CTA tap — twin of startBuyPurchase. */
  const startTipPurchase = () => {
    let base = tipBase;
    if (tipPadOpen) {
      const t = tipDraft.trim();
      if (t !== "") {
        const n = Number(t.replace(",", "."));
        const floor = minPriceFor(viewDj?.currency);
        const cap = maxPriceFor(viewDj?.currency, viewDj?.capsLifted);
        if (Number.isFinite(n) && n > 0) base = Math.min(Math.max(round2(n), floor), cap);
      }
      setTipDraft("");
      setTipPadOpen(false);
      setTipAmount(String(base));
    }
    const total = calculateFeeBreakdown(base, viewDj?.currency).customerTotal;

    const armed = tipWalletRef.current;
    if (!armed) {
      setTipCtaTapped(true); // no wallet on this device → classic reveal
      return;
    }
    const cents = Math.round(total * 100);
    if (cents !== armed.lastCents) {
      try {
        armed.pr.update({ total: { label: "Request App", amount: cents } });
        armed.lastCents = cents;
      } catch (e) {
        console.error("wallet update() failed:", e);
        setTipCtaTapped(true);
        return;
      }
    }
    let shown = false;
    try {
      armed.pr.show();
      shown = true;
    } catch (e) {
      console.error("wallet show() failed:", e);
    }
    if (!shown) {
      setTipCtaTapped(true);
      return;
    }

    const fresh =
      tipClientSecret && tipOrderId && tipSecretBaseRef.current === base;
    const secretPromise = fresh
      ? Promise.resolve({ clientSecret: tipClientSecret as string, orderId: tipOrderId as string })
      : paymentsApi.createTipIntent({
            djSlug: dj?.slug,
            amount: total,
            requestAmount: base,
            comment,
            clientNickname: clientNickname || undefined,
            clientInstagram: clientInstagram || undefined,
            orderId: tipOrderId || undefined,
          });
    secretPromise.catch(() => {});
    tipTapCtxRef.current = {
      secretPromise,
      cancelled: false,
      succeeded: false,
      uiSettled: false,
    };
  };

  return {
    tipMode,
    setTipMode,
    tipAmount,
    setTipAmount,
    tipPadOpen,
    setTipPadOpen,
    tipDraft,
    setTipDraft,
    tipCtaTapped,
    setTipCtaTapped,
    tipOrderId,
    setTipOrderId,
    tipClientSecret,
    setTipClientSecret,
    tipShowPay,
    setTipShowPay,
    tipShowCard,
    setTipShowCard,
    tipProcessing,
    setTipProcessing,
    tipPayError,
    setTipPayError,
    tipBase,
    tipTotal,
    tipTiles,
    tipTileSelected,
    tipIsCustom,
    commitTipPad,
    tipSucceeded,
    tipWalletMark,
    startTipPurchase,
  };
}

export type TipCheckout = ReturnType<typeof useTipCheckout>;
